<template>
  <div>
    <lf-checkbox-dropdown
      :model-value="modelValue"
      :options="userOptions"
      :name="name"
      :enable-search="enableSearch"
      :show-clear-filter="showClearFilter"
      :filter-icon="filterIcon"
      :get-paginated-options="getPaginatedUsers"
      api-query-param="search"
      lazy-load
      v-bind="omit($attrs, ['is-loading'])"
      @update:model-value="emit('update:modelValue', $event)"
      @loaded-more="handleLoadedMore"
      @options-search="handleOptionsSearch"
    />
  </div>
</template>
<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import omit from "lodash/omit";

import usersApiService, {
  type GetUsersByRoleParams,
} from "@/services/modules/users";

import LfCheckboxDropdown from "@/components/ui/inputs/LfCheckboxDropdown.vue";

import type { Role } from "@/models/options";
import type { IUser } from "@/models/users";

const emit = defineEmits<{
  "update:modelValue": unknown[];
}>();

const {
  modelValue,
  name,
  enableSearch,
  showClearFilter,
  filterIcon,
  showCurrentUserOption,
} = defineProps<{
  modelValue: number[];
  name: Role;
  enableSearch: boolean;
  showClearFilter: boolean;
  filterIcon: boolean;
  showCurrentUserOption?: boolean;
}>();

const { t } = useI18n();

const loadedUsers = ref<Record<string, string>>({});
const userOptions = ref<Record<string, string>>(getDefaultOptions());

function getDefaultOptions(): Record<string, string> {
  const options: Record<string, string> = { null: t("COMMON.NONE") };
  if (showCurrentUserOption) {
    options[-1] = t("COMMON.CURRENT_USER");
  }
  return options;
}

const getPaginatedUsers = (
  params: Record<string, string | string[] | number | number[]>
) => {
  const serviceParams: GetUsersByRoleParams = {
    role: name,
    page: params.page as number | undefined,
    search: params.search as string | undefined,
  };
  return usersApiService.getUsersByRole(serviceParams);
};

const mergeUsers = (users: IUser[]) => {
  const newOptions: Record<string, string> = {};
  for (const user of users) {
    if (user.id) {
      newOptions[user.id] = user.full_name || "";
    }
  }
  return newOptions;
};

const handleLoadedMore = (data: unknown[]) => {
  const newOptions = mergeUsers(data as IUser[]);
  loadedUsers.value = { ...loadedUsers.value, ...newOptions };
  userOptions.value = { ...getDefaultOptions(), ...loadedUsers.value };
};

const handleOptionsSearch = (data: unknown[]) => {
  const newOptions = mergeUsers(data as IUser[]);
  loadedUsers.value = { ...loadedUsers.value, ...newOptions };
  userOptions.value = { ...getDefaultOptions(), ...loadedUsers.value };
};
</script>
